import { Address } from './token'

/**
 * Backend Feather Order Status Enum
 * Maps to the backend status enum values
 */
export enum FeatherBackendStatus {
    UNKNOWN = 'unknown', // Unknown status
    PING = 'ping', // keep-alive ping
    ORDER_RECEIVED = 'order_received', // Order initially received
    PROCESSING = 'order_processing', // Order is being processed
    AWAITING_DEPOSIT = 'awaiting_deposit', // Waiting for user deposit
    DEPOSIT_TIMEOUT = 'deposit_timeout', // Deposit not received in time
    DEPOSIT_CONFIRMED = 'deposit_confirmed', // Deposit confirmed by Binance
    TRADING = 'trade_in_progress', // Trade is being executed
    TRADE_FAILED = 'trade_failed', // Trade failed
    TRADED = 'trade_completed', // Trade completed successfully (backend event)
    WITHDRAWING = 'withdrawal_in_progress', // Withdrawal is being processed
    WITHDRAWAL_PENDING = 'withdrawal_pending_tx', // Withdrawal transaction submitted, waiting for confirmation
    WITHDRAWAL_FAILED = 'withdrawal_failed', // Withdrawal failed
    COMPLETED = 'completed', // Order completed successfully
    FAILED = 'failed', // Order failed
}

/**
 * Error structure for Feather quote errors
 */
export interface FeatherQuoteError {
    /** Error message from the API */
    message: string
    /** Type of error for handling specific cases */
    type: 'amount_too_small' | 'calculation_error' | 'missing_address' | string
}

/**
 * Request payload for signing a Feather quote
 */
export interface IFeatherQuoteSignPayload {
    quote_id: string
    signature: string
    src_user_wallet: string
}

export interface IFeatherApiQuotePayload {
    quote_id?: string
    src_ticker: string
    src_chain: string
    dst_ticker: string
    dst_chain: string
    src_user_wallet: string
    dst_user_wallet: string
    src_amount: string
}

/**
 * Base interface for Feather quote responses
 */
interface IFeatherApiQuoteResponseBase {
    quote_id: string
    created_at: string
    expires_at: string

    src_chain: string
    src_ticker: string
    src_amount: string
    src_user_wallet: string

    src_deposit_wallet: string

    dst_chain: string
    dst_ticker: string
    dst_amount: string
    dst_user_wallet: string
}

/**
 * Response for a Feather quote request
 * ---------------- Example ----------------
    quote_id: '1ff3a902-bfc1-4c5f-b8bd-55823e46154c'
    created_at: '2025-03-25T06:45:54.126846'
    expires_at: '2025-03-25T07:00:54.126846'

    src_chain: 'arbitrum'
    src_ticker: 'USDC'
    src_amount: '3'
    src_user_wallet: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831'

    src_deposit_wallet: '0xb209532e47296b1608f4cce72f16b91b9b672a35'

    dst_chain: 'bitcoin'
    dst_ticker: 'BTC'
    dst_amount: '7.992000000000001e-05'
    dst_user_wallet: 'bc1paqglk025uslp2vnzyn88xqhf9740msym2zkr8fafjw4yj75az3hsll0449'

    signature: null
    status: 'partial'
*/
export interface IFeatherApiQuoteResponse extends IFeatherApiQuoteResponseBase {
    signature: string | null
    status: 'partial' | 'complete'
}

/**
 * Response for a signed Feather quote
 */
export interface IFeatherApiSignedQuoteResponse extends IFeatherApiQuoteResponseBase {
    signature: string // never null
    status: 'complete'
}

/**
 * Response for a Feather quote request
 */
export interface IFeatherFormattedQuoteResponse {
    /** Unique ID for the quote */
    quoteId: string

    /** The original amount in smallest unit (e.g., wei) */
    sellAmount: string

    /** The amount of BTC to be received in smallest unit (satoshis) */
    buyAmount: string

    /** How long the quote is valid for in seconds */
    validFor: number

    /** Timestamp when the quote expires */
    expiresAt: number

    /** Wallet address to deposit funds to (for the source chain) */
    srcDepositWallet: Address
}

/**
 * Response when creating a Feather order
 */
export interface IFeatherOrderCreateResponse {
    /** Unique ID for the order */
    orderId: string

    /** The address to send funds to */
    depositAddress: Address

    /** Optional memo ID for certain chains that require it */
    memoId?: string

    /** Timestamp when the order expires */
    expiresAt: number

    /** Amount to send in smallest unit */
    amountToSend: string

    /** Token address to send */
    tokenAddress: Address

    /** Chain ID to send from */
    chainId: number
}

/**
 * Processing history entry for a Feather order
 */
export interface IFeatherProcessingHistoryEntry {
    /** Timestamp when the status changed */
    timestamp: string
    /** Status at this point in time */
    status: FeatherBackendStatus
    /** Optional message associated with this status */
    message?: string
}

/**
 * Response when checking a Feather order status
 * Directly mirrors the backend OrderUpdate struct
 */
export interface IFeatherOrderStatusResponse {
    orderId: string
    status: FeatherBackendStatus
    createdAt: string
    updatedAt: string
    timeout: number
    // This may be null or undefined if src deposit hasn't occurred yet
    src_deposit_tx: string | null | undefined
    // This may be null or undefined if dst fill hasn't occurred yet
    dst_fill_tx: string | null | undefined
    processingHistory: IFeatherProcessingHistoryEntry[]
    quoteId: string
    srcTicker: string
    srcChain: string
    dstTicker: string
    dstChain: string
    srcUserWallet: string
    dstUserWallet: string
    srcDepositWallet: string
    srcAmount: string
    dstAmount: string
    quoteExpiresAt: string
}
